/**
 * Storage facade for Macro Score weight presets.
 *
 * Thin wrapper around the axios service functions in services/api.js, mirroring
 * ScoringBuilder/scoringPresetStore.js. A macro preset is just slider state:
 * { name, weights: { economic_health: {...}, market_sentiment: {...} } }.
 */

import {
  getMacroScorePresets,
  getMacroScorePreset,
  saveMacroScorePreset,
  updateMacroScorePreset,
  deleteMacroScorePreset,
  setDefaultMacroScorePreset,
  clearDefaultMacroScorePreset,
} from '../../../services/api';

const PRESET_EXPORT_VERSION = 1;
const SCHEMA_VERSION = 1;

function normalizeForSave(payload) {
  return {
    name: (payload.name || '').trim(),
    weights: payload.weights || {},
    schema_version: payload.schema_version ?? SCHEMA_VERSION,
  };
}

/** List all macro-score presets owned by the current user. */
export async function listPresets() {
  const { data } = await getMacroScorePresets();
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
}

/** Fetch one preset by ID. */
export async function fetchPreset(id) {
  const { data } = await getMacroScorePreset(id);
  return data;
}

/** Create a new preset. */
export async function createPreset(payload) {
  const { data } = await saveMacroScorePreset(normalizeForSave(payload));
  return data;
}

/** Replace an existing preset (PUT). */
export async function savePreset(id, payload) {
  const { data } = await updateMacroScorePreset(id, normalizeForSave(payload));
  return data;
}

/** Delete a preset by ID. */
export async function removePreset(id) {
  await deleteMacroScorePreset(id);
}

/** Mark a preset as the user's default. */
export async function markDefault(id) {
  const { data } = await setDefaultMacroScorePreset(id);
  return data; // { default_macro_score_preset_id }
}

/** Clear the user's default-preset selection. */
export async function clearDefault(id) {
  const { data } = await clearDefaultMacroScorePreset(id);
  return data; // { default_macro_score_preset_id: null }
}

// --- JSON export / import (client-side; no backend hit) --------------------

export function exportPresetToJson(preset) {
  return JSON.stringify(
    {
      export_version: PRESET_EXPORT_VERSION,
      schema_version: preset.schema_version ?? SCHEMA_VERSION,
      name: preset.name,
      weights: preset.weights || {},
    },
    null,
    2,
  );
}

export function parsePresetFromJson(jsonString) {
  let parsed;
  try {
    parsed = JSON.parse(jsonString);
  } catch (err) {
    throw new Error('Could not parse JSON: ' + err.message);
  }
  if (!parsed || typeof parsed !== 'object') throw new Error('Imported file is not an object');
  if (parsed.export_version !== PRESET_EXPORT_VERSION) {
    throw new Error(
      `Unsupported export_version ${parsed.export_version}. This client expects ${PRESET_EXPORT_VERSION}.`,
    );
  }
  if (typeof parsed.name !== 'string' || !parsed.name.trim()) {
    throw new Error('Imported preset is missing a name');
  }
  if (!parsed.weights || typeof parsed.weights !== 'object') {
    throw new Error('Imported preset has no weights');
  }
  return {
    name: parsed.name,
    weights: parsed.weights,
    schema_version: parsed.schema_version ?? SCHEMA_VERSION,
  };
}
